import React, { useEffect, useState } from 'react';
import { motion, useAnimation, easeOut } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Handshake, Repeat } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeInUpVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeOut } },
};

const BrandPillars: React.FC = () => {
  const [content, setContent] = useState<any>(null);
  const { t_api } = useLanguage();
  const [refHeading, inViewHeading] = useInView({ triggerOnce: false, threshold: 0.2 });
  const controlsHeading = useAnimation();
  const controlsUnderline = useAnimation();
  const [refCards, inViewCards] = useInView({ triggerOnce: false, threshold: 0.2 });
  const controlsCards = useAnimation();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => setContent(data['Brand Pillars'] || {}))
      .catch(err => console.warn('Brand pillars content unavailable:', err));
  }, []);

  useEffect(() => {
    if (inViewHeading) {
      controlsHeading.start('visible');
      controlsUnderline.start({ scaleX: 1 });
    } else {
      controlsHeading.start('hidden');
      controlsUnderline.start({ scaleX: 0 });
    }
  }, [inViewHeading, controlsHeading, controlsUnderline]);

  useEffect(() => {
    inViewCards ? controlsCards.start('visible') : controlsCards.start('hidden');
  }, [inViewCards, controlsCards]);

  const defaultTitles = [
    { title: t_api(content?.pillar_1_title || 'Excellence', content?.pillar_1_title_ar || 'التميز'), icon: Award, desc: t_api(content?.pillar_1_desc || 'Excellence is the essence in all that we do.', content?.pillar_1_desc_ar || 'التميز هو الجوهر في كل ما نقوم به.') },
    { title: t_api(content?.pillar_2_title || 'Commitment', content?.pillar_2_title_ar || 'الالتزام'), icon: Handshake, desc: t_api(content?.pillar_2_desc || 'We promise to achieve our goals whilst remaining committed.', content?.pillar_2_desc_ar || 'نعد بتحقيق أهدافنا مع البقاء ملتزمين.') },
    { title: t_api(content?.pillar_3_title || 'Consistent', content?.pillar_3_title_ar || 'الاستمرارية'), icon: Repeat, desc: t_api(content?.pillar_3_desc || 'Produce work that looks good and serves a purpose.', content?.pillar_3_desc_ar || 'إنتاج أعمال تتميز بالجودة وتخدم الغرض.') },
  ];

  return (
    <section className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-2 sm:px-8">
        <motion.div ref={refHeading} variants={fadeInUpVariant} initial="hidden" animate={controlsHeading} className="text-center mb-8">
          <h2 className="text-3xl font-bold text-black mb-2">{t_api(content?.title || 'Brand Pillars', content?.title_ar || 'ركائز العلامة التجارية')}</h2>
          <motion.div className="w-20 h-1 bg-[#F2BB13] mx-auto" initial={{ scaleX: 0 }} animate={controlsUnderline} transition={{ duration: 0.5 }} style={{ transformOrigin: 'center' }} />
        </motion.div>

        <div ref={refCards} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {defaultTitles.map((item, index) => (
            <motion.div key={index} variants={fadeInUpVariant} initial="hidden" animate={controlsCards} transition={{ delay: 0.1 * index }} className="bg-white p-6 border border-gray-200 rounded-lg shadow-md text-center">
              <item.icon className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-red-600 mb-2">{item.title}</h3>
              <p className="text-gray-700">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandPillars;
